import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Phone, Mail, MapPin, Clock, Menu, X, Calendar, MessageCircle,
  Heart, Brain, Users, Sparkles, ChevronRight, Star, ArrowRight,
  CheckCircle, Send, ShoppingCart, CreditCard, Shield, Download, Package,
  Lock, LogOut, BarChart3, DollarSign, UserCheck, MessageSquare, Eye, Trash2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3377a0f-3b51-472d-8721-17e7691f2fff/artifacts/natw4rye_logo%20online.png";

// Contact info
const CONTACT = {
  phone1: "644 299 079",
  email: "consulta@casaleizpsicologo.es",
  address: "Don Cristian 19, 4ºB",
  city: "29007 Málaga",
  hours: "Lunes-Viernes: 09:00 - 20:00",
  whatsapp: "34644299079"
};

// Services data
const SERVICES = [
  {
    id: "psicoterapia",
    icon: Brain,
    title: "Psicoterapia Individual",
    description: "Tratamiento personalizado para superar problemas emocionales, traumas y mejorar tu bienestar psicológico.",
    features: ["Depresión", "Ansiedad", "Traumas", "Fobias"]
  },
  {
    id: "pareja",
    icon: Heart,
    title: "Terapia de Pareja",
    description: "Mejora la comunicación, resuelve conflictos y reconecta emocionalmente con tu pareja.",
    features: ["Crisis de pareja", "Comunicación", "Infidelidad", "Ruptura"]
  },
  {
    id: "dependencia",
    icon: Users,
    title: "Dependencia Emocional",
    description: "Aprende a construir relaciones sanas y desarrolla una autoestima sólida e independiente.",
    features: ["Apego ansioso", "Miedo al abandono", "Relaciones tóxicas"]
  },
  {
    id: "autoestima",
    icon: Sparkles,
    title: "Autoestima",
    description: "Fortalece tu autoconcepto, supera la inseguridad y desarrolla confianza en ti mismo.",
    features: ["Inseguridad", "Autoaceptación", "Crecimiento personal"]
  }
];

// Testimonials from Doctoralia
const TESTIMONIALS = [
  {
    name: "Claudio",
    text: "Es un gran profesional y gran persona. Cercano, empático, amable y te aporta herramientas útiles para poder lidiar con tus problemas de manera resolutiva y eficaz. Yo estoy encantado con su trato.",
    rating: 5,
    service: "Terapia de pareja",
    verified: true
  },
  {
    name: "Javier L.",
    text: "Solo puedo estar agradecido. Carlos es un fiera y me siento un afortunado de haber dado con él. Buen profesional, buen trato, puntual y un sitio muy acogedor. Todo de 10.",
    rating: 5,
    service: "Visita Psicología",
    verified: true
  },
  {
    name: "María",
    text: "Me siento muy agradecida de haber encontrado a un profesional como Carlos. Cada sesión me ayuda a entenderme mejor y a avanzar, siempre desde la cercanía, el respeto y la profesionalidad. Sin duda, es un gran psicólogo y una gran persona.",
    rating: 5,
    service: "Psicoterapia",
    verified: true
  },
  {
    name: "M.A.",
    text: "Excelente enfoque, resuena mucho con mi propia mentalidad y creo que me va a ayudar genuinamente a enfrentarme a mis problemas.",
    rating: 5,
    service: "Primera visita",
    verified: true
  },
  {
    name: "SVM",
    text: "Muy contenta. Es mi primera visita a un psicólogo y no me ha costado nada hablar y expresarme. Una comprensión en todo momento y eso lo hacía todo fácil. Muy bien.",
    rating: 5,
    service: "Primera visita",
    verified: true
  },
  {
    name: "Aurora F.R.",
    text: "Me ha ayudado mucho, es muy bueno y lo recomiendo a todos.",
    rating: 5,
    service: "Psicoterapia",
    verified: true
  }
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: "Inicio" },
    { to: "/servicios", label: "Servicios" },
    { to: "/sobre-nosotros", label: "Sobre Nosotros" },
    { to: "/blog", label: "Blog" },
    { to: "/tienda", label: "Tienda" },
    { to: "/contacto", label: "Contacto" }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-lg" : "bg-transparent"
      }`}
      data-testid="main-navigation"
    >
      <div className="container flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <img src={LOGO_URL} alt="Psicólogos Online - Casaleiz Psicología" className="h-12 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors hover:text-[#1B4D3E] ${
                location.pathname === link.to ? "text-[#1B4D3E]" : "text-stone-600"
              }`}
              data-testid={`nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/reservar"
            className="btn-primary text-sm py-3 px-6"
            data-testid="nav-cta-reservar"
          >
            <Calendar className="w-4 h-4" />
            Reservar Cita
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 text-[#1B4D3E]"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass border-t border-stone-100"
            data-testid="mobile-menu"
          >
            <div className="container py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-stone-600 hover:text-[#1B4D3E] py-2 font-medium"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(" ", "-")}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/reservar"
                className="btn-primary text-center mt-2"
                data-testid="mobile-nav-cta-reservar"
              >
                <Calendar className="w-4 h-4" />
                Reservar Cita
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-[#1B4D3E] text-white py-16" data-testid="footer">
    <div className="container">
      <div className="grid md:grid-cols-4 gap-12">
        <div>
          <div className="mb-4">
            <img src={LOGO_URL} alt="Psicólogos Online" className="h-14 w-auto" />
          </div>
          <p className="text-white/70 text-sm leading-relaxed">
            Psicólogos online especializados en terapia por videollamada. Atención psicológica profesional desde cualquier lugar.
          </p>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-4">Servicios</h4>
          <ul className="space-y-2 text-white/70 text-sm">
            <li><Link to="/servicios" className="hover:text-white transition-colors">Psicoterapia Individual</Link></li>
            <li><Link to="/servicios" className="hover:text-white transition-colors">Terapia de Pareja</Link></li>
            <li><Link to="/servicios" className="hover:text-white transition-colors">Dependencia Emocional</Link></li>
            <li><Link to="/servicios" className="hover:text-white transition-colors">Autoestima</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-4">Contacto</h4>
          <ul className="space-y-3 text-white/70 text-sm">
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <a href={`tel:${CONTACT.phone1.replace(/\s/g, "")}`} className="hover:text-white transition-colors">{CONTACT.phone1}</a>
            </li>
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors">{CONTACT.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5" />
              <span>{CONTACT.address}<br />{CONTACT.city}</span>
            </li>
            <li className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{CONTACT.hours}</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-serif text-lg mb-4">Horario</h4>
          <p className="text-white/70 text-sm mb-4">
            Lunes a Viernes<br />
            09:00 - 20:00
          </p>
          <Link
            to="/reservar"
            className="inline-flex items-center gap-2 bg-white text-[#1B4D3E] px-6 py-3 rounded-full font-medium text-sm hover:bg-white/90 transition-colors"
            data-testid="footer-cta-reservar"
          >
            <Calendar className="w-4 h-4" />
            Reservar Cita
          </Link>
        </div>
      </div>

      <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-white/50 text-sm">
        <p>© 2025 Casaleiz Psicología. Todos los derechos reservados.</p>
        <div className="flex gap-6">
          <Link to="/privacidad" className="hover:text-white transition-colors">Política de Privacidad</Link>
          <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
        </div>
      </div>
    </div>
  </footer>
);

// WhatsApp Button
const WhatsAppButton = () => (
  <a
    href={`https://wa.me/${CONTACT.whatsapp}?text=Hola,%20me%20gustaría%20solicitar%20información%20sobre%20sus%20servicios%20de%20psicología.`}
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    data-testid="whatsapp-button"
    aria-label="Contactar por WhatsApp"
  >
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

// Home Page
const HomePage = () => {
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setBlogPosts(response.data.slice(0, 3));
      } catch (e) {
        console.error("Error fetching blog posts:", e);
      }
    };
    fetchBlogPosts();
  }, []);

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-gradient min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.span
                variants={fadeInUp}
                className="inline-block text-sm font-medium tracking-wide uppercase text-[#1B4D3E] bg-[#E0F2F1] px-4 py-2 rounded-full mb-6"
              >
                Psicólogos Online
              </motion.span>
              <motion.h1
                variants={fadeInUp}
                className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight leading-[1.1] mb-6"
              >
                Terapia psicológica <span className="text-[#1B4D3E]">online profesional</span>
              </motion.h1>
              <motion.p
                variants={fadeInUp}
                className="text-lg md:text-xl leading-relaxed text-stone-600 mb-8 max-w-lg"
              >
                Psicólogos online especializados en terapia por videollamada. Supera la ansiedad, mejora tus relaciones y encuentra el equilibrio que necesitas desde casa.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Link to="/reservar" className="btn-primary" data-testid="hero-cta-reservar">
                  <Calendar className="w-5 h-5" />
                  Reservar Primera Consulta
                </Link>
                <Link to="/servicios" className="btn-outline" data-testid="hero-cta-servicios">
                  Conocer Servicios
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </motion.div>
              <motion.div variants={fadeInUp} className="mt-8 flex items-center gap-6 text-sm text-stone-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1B4D3E]" />
                  <span>Profesionales colegiados</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1B4D3E]" />
                  <span>100% Online desde casa</span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1713865469900-d12502a39875?w=800&q=80"
                  alt="Sesión de terapia psicológica online - Psicólogos Online"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/20 to-transparent"></div>
              </div>
              {/* Floating card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-6 -left-6 glass-card p-6 rounded-2xl max-w-xs"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex -space-x-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-[#E0F2F1] border-2 border-white flex items-center justify-center">
                        <Star className="w-4 h-4 text-[#D4A373]" />
                      </div>
                    ))}
                  </div>
                  <span className="text-sm font-medium text-stone-700">+500 pacientes</span>
                </div>
                <p className="text-sm text-stone-600">Valoración excelente en Doctoralia</p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section bg-white" data-testid="services-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-sm font-medium tracking-wide uppercase text-[#1B4D3E]">
              Nuestros Servicios
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mt-4 mb-4">
              Cómo podemos ayudarte
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-stone-600 max-w-2xl mx-auto">
              Ofrecemos atención psicológica personalizada, tanto online como presencial, adaptada a tus necesidades específicas.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {SERVICES.map((service) => (
              <motion.div
                key={service.id}
                variants={fadeInUp}
                className="card group cursor-pointer"
                data-testid={`service-card-${service.id}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-[#E0F2F1] flex items-center justify-center mb-6 group-hover:bg-[#1B4D3E] transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-[#1B4D3E] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-serif mb-3">{service.title}</h3>
                <p className="text-stone-600 text-sm mb-4">{service.description}</p>
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature, i) => (
                    <span key={i} className="text-xs bg-stone-100 text-stone-600 px-3 py-1 rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <Link to="/servicios" className="btn-secondary" data-testid="services-cta-ver-todos">
              Ver todos los servicios
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="section bg-[#F5F5F4]" data-testid="about-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?w=600&q=80"
                  alt="Psicólogo online profesional"
                  className="rounded-3xl w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-lg">
                  <p className="text-4xl font-serif text-[#1B4D3E] font-medium">+15</p>
                  <p className="text-sm text-stone-600">Años de experiencia</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="text-sm font-medium tracking-wide uppercase text-[#1B4D3E]">
                Sobre Nosotros
              </motion.span>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mt-4 mb-6">
                Psicólogos online comprometidos con tu bienestar
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-stone-600 mb-6 leading-relaxed">
                En Psicólogos Online entendemos que dar el paso de buscar ayuda profesional requiere valentía. Nuestro equipo de psicólogos online está especializado en proporcionar un espacio seguro y confidencial donde puedas trabajar en tu bienestar emocional desde la comodidad de tu hogar.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-stone-600 mb-8 leading-relaxed">
                Ofrecemos terapia psicológica online por videollamada, adaptándonos a tus necesidades y horarios. Atendemos a pacientes de toda España.
              </motion.p>
              <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
                <Link to="/sobre-nosotros" className="btn-primary" data-testid="about-cta-conocenos">
                  Conoce nuestro equipo
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section bg-white" data-testid="testimonials-section">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span variants={fadeInUp} className="text-sm font-medium tracking-wide uppercase text-[#1B4D3E]">
              Testimonios
            </motion.span>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mt-4 mb-4">
              Lo que dicen nuestros pacientes
            </motion.h2>
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-4 mt-6">
              <a 
                href="https://www.doctoralia.es/carlos-casaleiz/psicologo/malaga" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#00A89D] text-white px-6 py-3 rounded-full hover:bg-[#008F86] transition-colors"
              >
                <span className="font-bold text-2xl">5.0</span>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-white text-white" />
                  ))}
                </div>
                <span className="text-sm font-medium">238 opiniones en Doctoralia</span>
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {TESTIMONIALS.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-[#F5F5F4] p-6 rounded-3xl"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#D4A373] text-[#D4A373]" />
                    ))}
                  </div>
                  {testimonial.verified && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#00A89D] font-medium">
                      <CheckCircle className="w-3 h-3" />
                      Verificado
                    </span>
                  )}
                </div>
                <p className="text-stone-600 mb-4 leading-relaxed text-sm">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <p className="font-medium text-stone-800">{testimonial.name}</p>
                  <span className="text-xs text-stone-500">{testimonial.service}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-10"
          >
            <a
              href="https://www.doctoralia.es/carlos-casaleiz/psicologo/malaga#profile-reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#1B4D3E] font-medium hover:underline"
            >
              Ver todas las opiniones en Doctoralia
              <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Blog Section */}
      {blogPosts.length > 0 && (
        <section className="section bg-[#F5F5F4]" data-testid="blog-section">
          <div className="container">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.span variants={fadeInUp} className="text-sm font-medium tracking-wide uppercase text-[#1B4D3E]">
                Blog
              </motion.span>
              <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mt-4 mb-4">
                Artículos sobre psicología
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-stone-600 max-w-2xl mx-auto">
                Recursos y consejos de nuestros psicólogos online para tu bienestar emocional.
              </motion.p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-8"
            >
              {blogPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  variants={fadeInUp}
                  className="bg-white rounded-3xl overflow-hidden group"
                  data-testid={`blog-post-${index}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={post.imagen}
                      alt={post.titulo}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-xs font-medium text-[#1B4D3E] px-3 py-1 rounded-full">
                      {post.categoria}
                    </span>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-serif mb-3 group-hover:text-[#1B4D3E] transition-colors">
                      {post.titulo}
                    </h3>
                    <p className="text-stone-600 text-sm mb-4 line-clamp-2">{post.extracto}</p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-sm font-medium text-[#1B4D3E] hover:gap-3 transition-all"
                    >
                      Leer más
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center mt-12"
            >
              <Link to="/blog" className="btn-secondary" data-testid="blog-cta-ver-todos">
                Ver todos los artículos
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section bg-[#1B4D3E]" data-testid="cta-section">
        <div className="container text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif text-white mb-6">
              ¿Listo para empezar tu camino hacia el bienestar?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-white/80 mb-8 max-w-2xl mx-auto">
              Da el primer paso. Reserva tu consulta y descubre cómo podemos ayudarte.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/reservar"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#1B4D3E] px-8 py-4 rounded-full font-medium hover:bg-white/90 transition-colors"
                data-testid="cta-reservar"
              >
                <Calendar className="w-5 h-5" />
                Reservar Cita
              </Link>
              <a
                href={`tel:${CONTACT.phone1.replace(/\s/g, "")}`}
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 rounded-full font-medium hover:bg-white/10 transition-colors"
                data-testid="cta-llamar"
              >
                <Phone className="w-5 h-5" />
                {CONTACT.phone1}
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

// Services Page
const ServicesPage = () => {
  const allServices = [
    ...SERVICES,
    {
      id: "ansiedad",
      icon: Brain,
      title: "Ansiedad y Estrés",
      description: "Tratamiento especializado para trastornos de ansiedad, ataques de pánico, fobias y manejo del estrés crónico.",
      features: ["Ataques de pánico", "Fobia social", "Estrés laboral", "TAG"]
    },
    {
      id: "depresion",
      icon: Heart,
      title: "Depresión",
      description: "Acompañamiento profesional para superar la depresión y recuperar la motivación y alegría de vivir.",
      features: ["Tristeza persistente", "Apatía", "Duelo", "Crisis vital"]
    }
  ];

  return (
    <main className="pt-20" data-testid="services-page">
      <section className="section hero-gradient">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.span variants={fadeInUp} className="inline-block text-sm font-medium tracking-wide uppercase text-[#1B4D3E] bg-[#E0F2F1] px-4 py-2 rounded-full mb-6">
              Nuestros Servicios
            </motion.span>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif mb-6">
              Servicios de psicología online
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-stone-600">
              Ofrecemos una amplia gama de servicios psicológicos online adaptados a tus necesidades. Terapia por videollamada con profesionales especializados.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {allServices.map((service, index) => (
              <motion.div
                key={service.id}
                variants={fadeInUp}
                className="card group"
                data-testid={`service-detail-${service.id}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-[#E0F2F1] flex items-center justify-center mb-6 group-hover:bg-[#1B4D3E] transition-colors duration-300">
                  <service.icon className="w-8 h-8 text-[#1B4D3E] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-2xl font-serif mb-4">{service.title}</h3>
                <p className="text-stone-600 mb-6">{service.description}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {service.features.map((feature, i) => (
                    <span key={i} className="text-sm bg-stone-100 text-stone-600 px-4 py-2 rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
                <Link
                  to="/reservar"
                  className="inline-flex items-center gap-2 text-[#1B4D3E] font-medium hover:gap-3 transition-all"
                >
                  Solicitar cita
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Modality Section */}
      <section className="section bg-[#F5F5F4]">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-12"
          >
            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-3xl">
              <div className="w-14 h-14 rounded-2xl bg-[#E0F2F1] flex items-center justify-center mb-6">
                <MessageCircle className="w-7 h-7 text-[#1B4D3E]" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Terapia Online</h3>
              <p className="text-stone-600 mb-4">
                Sesiones de psicoterapia por videollamada desde la comodidad de tu hogar. Misma calidad profesional, máxima flexibilidad.
              </p>
              <ul className="space-y-2 text-stone-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1B4D3E]" />
                  Sin desplazamientos
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1B4D3E]" />
                  Horarios flexibles
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1B4D3E]" />
                  Total confidencialidad
                </li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="bg-white p-8 rounded-3xl">
              <div className="w-14 h-14 rounded-2xl bg-[#E0F2F1] flex items-center justify-center mb-6">
                <MapPin className="w-7 h-7 text-[#1B4D3E]" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Terapia Presencial</h3>
              <p className="text-stone-600 mb-4">
                Visítanos en nuestra consulta en el centro de Málaga. Un espacio acogedor y confidencial para tu bienestar.
              </p>
              <ul className="space-y-2 text-stone-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1B4D3E]" />
                  C/ Don Cristian 19, 4ºB
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1B4D3E]" />
                  Centro de Málaga
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#1B4D3E]" />
                  Fácil acceso
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[#1B4D3E]">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
            ¿Necesitas ayuda profesional?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Reserva tu consulta y comienza tu camino hacia el bienestar.
          </p>
          <Link
            to="/reservar"
            className="inline-flex items-center gap-2 bg-white text-[#1B4D3E] px-8 py-4 rounded-full font-medium hover:bg-white/90 transition-colors"
            data-testid="services-page-cta-reservar"
          >
            <Calendar className="w-5 h-5" />
            Reservar Consulta
          </Link>
        </div>
      </section>
    </main>
  );
};

// About Page
const AboutPage = () => (
  <main className="pt-20" data-testid="about-page">
    <section className="section hero-gradient">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.span variants={fadeInUp} className="inline-block text-sm font-medium tracking-wide uppercase text-[#1B4D3E] bg-[#E0F2F1] px-4 py-2 rounded-full mb-6">
              Sobre Nosotros
            </motion.span>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif mb-6">
              Psicólogos Online - Casaleiz Psicología
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-stone-600 mb-6 leading-relaxed">
              Somos un equipo de psicólogos online comprometidos con proporcionar atención psicológica de calidad a través de videollamada, llegando a pacientes de toda España.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-stone-600 leading-relaxed">
              Con más de 15 años de experiencia, hemos ayudado a cientos de personas a superar sus dificultades emocionales y mejorar su calidad de vida. Creemos en un enfoque personalizado, donde cada persona es única y merece un tratamiento adaptado a sus necesidades específicas.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <img
              src="https://images.unsplash.com/photo-1648737966330-111e62fd798a?w=600&q=80"
              alt="Terapia psicológica online - Psicólogos Online"
              className="rounded-3xl w-full h-[500px] object-cover shadow-2xl"
            />
          </motion.div>
        </div>
      </div>
    </section>

    <section className="section bg-white">
      <div className="container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-serif mb-4">
            Nuestra filosofía
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-stone-600 max-w-2xl mx-auto">
            Creemos en una psicología cercana, accesible y eficaz.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {[
            { title: "Profesionalidad", desc: "Formación continua y enfoque basado en la evidencia científica." },
            { title: "Cercanía", desc: "Creamos un espacio seguro donde te sentirás escuchado y comprendido." },
            { title: "Confidencialidad", desc: "Tu privacidad es nuestra máxima prioridad. Total discreción garantizada." }
          ].map((item, i) => (
            <motion.div key={i} variants={fadeInUp} className="text-center p-8">
              <div className="w-16 h-16 rounded-full bg-[#E0F2F1] flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-[#1B4D3E]" />
              </div>
              <h3 className="text-xl font-serif mb-3">{item.title}</h3>
              <p className="text-stone-600">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>

    <section className="section bg-[#F5F5F4]">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <img
              src="https://images.unsplash.com/photo-1622131104623-780b3e1b3f67?w=600&q=80"
              alt="Consulta online desde casa - Psicólogos Online"
              className="rounded-3xl w-full h-[400px] object-cover"
            />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-serif mb-6">
              Nuestra consulta en Málaga
            </h2>
            <p className="text-stone-600 mb-6 leading-relaxed">
              Estamos ubicados en pleno centro de Málaga, en un espacio diseñado para tu comodidad y bienestar. Un ambiente tranquilo y acogedor donde podrás expresarte con total libertad.
            </p>
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3 text-stone-600">
                <MapPin className="w-5 h-5 text-[#1B4D3E]" />
                <span>C/ Don Cristian 19, 4ºB - 29007 Málaga</span>
              </div>
              <div className="flex items-center gap-3 text-stone-600">
                <Phone className="w-5 h-5 text-[#1B4D3E]" />
                <span>{CONTACT.phone1}</span>
              </div>
              <div className="flex items-center gap-3 text-stone-600">
                <Clock className="w-5 h-5 text-[#1B4D3E]" />
                <span>{CONTACT.hours}</span>
              </div>
            </div>
            <Link to="/contacto" className="btn-primary" data-testid="about-cta-contacto">
              Cómo llegar
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  </main>
);

// Blog Page
const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // Seed posts first
        await axios.post(`${API}/blog/seed`);
        const response = await axios.get(`${API}/blog`);
        setPosts(response.data);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  return (
    <main className="pt-20" data-testid="blog-page">
      <section className="section hero-gradient">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.span variants={fadeInUp} className="inline-block text-sm font-medium tracking-wide uppercase text-[#1B4D3E] bg-[#E0F2F1] px-4 py-2 rounded-full mb-6">
              Blog
            </motion.span>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif mb-6">
              Artículos de psicología online
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-stone-600">
              Recursos, consejos y artículos de nuestros psicólogos online para cuidar tu salud mental.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#E0F2F1] border-t-[#1B4D3E] rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  variants={fadeInUp}
                  className="bg-white rounded-3xl overflow-hidden border border-stone-100 group hover:shadow-lg transition-shadow"
                  data-testid={`blog-article-${index}`}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={post.imagen}
                      alt={post.titulo}
                      className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-xs font-medium text-[#1B4D3E] px-3 py-1 rounded-full">
                      {post.categoria}
                    </span>
                  </div>
                  <div className="p-6">
                    <h2 className="text-xl font-serif mb-3 group-hover:text-[#1B4D3E] transition-colors">
                      {post.titulo}
                    </h2>
                    <p className="text-stone-600 text-sm mb-4 line-clamp-3">{post.extracto}</p>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-sm font-medium text-[#1B4D3E] hover:gap-3 transition-all"
                    >
                      Leer artículo completo
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
};

// Blog Post Page
const BlogPostPage = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const slug = location.pathname.split("/").pop();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="w-12 h-12 border-4 border-[#E0F2F1] border-t-[#1B4D3E] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Artículo no encontrado</h1>
          <Link to="/blog" className="btn-primary">Volver al blog</Link>
        </div>
      </div>
    );
  }

  return (
    <main className="pt-20" data-testid="blog-post-page">
      <article className="section">
        <div className="container max-w-4xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-8">
              <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#1B4D3E] mb-6">
                <ArrowRight className="w-4 h-4 rotate-180" />
                Volver al blog
              </Link>
              <span className="inline-block bg-[#E0F2F1] text-[#1B4D3E] text-sm font-medium px-4 py-1 rounded-full mb-4">
                {post.categoria}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-6">{post.titulo}</h1>
              <p className="text-lg text-stone-600">{post.extracto}</p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <img
                src={post.imagen}
                alt={post.titulo}
                className="w-full h-[400px] object-cover rounded-3xl"
              />
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-stone-800 prose-p:text-stone-600 prose-a:text-[#1B4D3E]"
            >
              {post.contenido.split("\n\n").map((paragraph, i) => {
                if (paragraph.startsWith("## ")) {
                  return <h2 key={i} className="text-2xl font-serif mt-8 mb-4">{paragraph.replace("## ", "")}</h2>;
                }
                if (paragraph.startsWith("### ")) {
                  return <h3 key={i} className="text-xl font-serif mt-6 mb-3">{paragraph.replace("### ", "")}</h3>;
                }
                return <p key={i} className="mb-4 leading-relaxed">{paragraph}</p>;
              })}
            </motion.div>

            <motion.div variants={fadeInUp} className="mt-12 p-8 bg-[#F5F5F4] rounded-3xl text-center">
              <h3 className="text-2xl font-serif mb-4">¿Necesitas ayuda profesional?</h3>
              <p className="text-stone-600 mb-6">Nuestro equipo de psicólogos online está aquí para ayudarte.</p>
              <Link to="/reservar" className="btn-primary" data-testid="blog-post-cta-reservar">
                <Calendar className="w-5 h-5" />
                Reservar Consulta
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </article>
    </main>
  );
};

// Contact Page
const ContactPage = () => {
  const [formData, setFormData] = useState({ nombre: "", email: "", telefono: "", mensaje: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      setFormData({ nombre: "", email: "", telefono: "", mensaje: "" });
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-20" data-testid="contact-page">
      <section className="section hero-gradient">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.span variants={fadeInUp} className="inline-block text-sm font-medium tracking-wide uppercase text-[#1B4D3E] bg-[#E0F2F1] px-4 py-2 rounded-full mb-6">
                Contacto
              </motion.span>
              <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif mb-6">
                Ponte en contacto con nosotros
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-lg text-stone-600 mb-8">
                Estamos aquí para ayudarte. Contacta con nuestro equipo de psicólogos online.
              </motion.p>

              <motion.div variants={fadeInUp} className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E0F2F1] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#1B4D3E]" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Teléfono</h3>
                    <p className="text-stone-600">
                      <a href={`tel:${CONTACT.phone1.replace(/\s/g, "")}`} className="hover:text-[#1B4D3E]">{CONTACT.phone1}</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E0F2F1] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#1B4D3E]" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Email</h3>
                    <p className="text-stone-600">
                      <a href={`mailto:${CONTACT.email}`} className="hover:text-[#1B4D3E]">{CONTACT.email}</a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E0F2F1] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#1B4D3E]" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Dirección</h3>
                    <p className="text-stone-600">{CONTACT.address}<br />{CONTACT.city}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E0F2F1] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#1B4D3E]" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Horario</h3>
                    <p className="text-stone-600">{CONTACT.hours}</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-8 rounded-3xl shadow-lg"
            >
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-[#E0F2F1] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-[#1B4D3E]" />
                  </div>
                  <h3 className="text-2xl font-serif mb-4">¡Mensaje enviado!</h3>
                  <p className="text-stone-600 mb-6">Nos pondremos en contacto contigo lo antes posible.</p>
                  <button onClick={() => setSubmitted(false)} className="btn-secondary">
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h3 className="text-2xl font-serif mb-6">Envíanos un mensaje</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="form-label">Nombre *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        required
                        data-testid="contact-input-nombre"
                      />
                    </div>
                    <div>
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        className="form-input"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="contact-input-email"
                      />
                    </div>
                    <div>
                      <label className="form-label">Teléfono</label>
                      <input
                        type="tel"
                        className="form-input"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        data-testid="contact-input-telefono"
                      />
                    </div>
                    <div>
                      <label className="form-label">Mensaje *</label>
                      <textarea
                        className="form-input min-h-[120px] resize-none"
                        value={formData.mensaje}
                        onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                        required
                        data-testid="contact-input-mensaje"
                      />
                    </div>
                    <button
                      type="submit"
                      className="btn-primary w-full"
                      disabled={submitting}
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? "Enviando..." : (
                        <>
                          <Send className="w-5 h-5" />
                          Enviar mensaje
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-[400px]">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3198.0!2d-4.4216!3d36.7213!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDQzJzE2LjciTiA0wrAyNScxNy44Ilc!5e0!3m2!1ses!2ses!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Ubicación Casaleiz Psicología en Málaga"
        />
      </section>
    </main>
  );
};

// Booking Page
const BookingPage = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    servicio: "",
    modalidad: "",
    fecha: "",
    hora: "",
    mensaje: ""
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const servicios = [
    "Psicoterapia Individual",
    "Terapia de Pareja",
    "Dependencia Emocional",
    "Autoestima",
    "Ansiedad y Estrés",
    "Depresión"
  ];

  const fetchSlots = async (fecha) => {
    try {
      const response = await axios.get(`${API}/appointments/available-slots?fecha=${fecha}`);
      setAvailableSlots(response.data.available_slots);
    } catch (e) {
      console.error("Error:", e);
      setAvailableSlots(["09:00", "10:00", "11:00", "12:00", "16:00", "17:00", "18:00", "19:00"]);
    }
  };

  const handleDateChange = (e) => {
    const fecha = e.target.value;
    setFormData({ ...formData, fecha, hora: "" });
    if (fecha) fetchSlots(fecha);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, formData);
      setSubmitted(true);
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = new Date().toISOString().split("T")[0];

  if (submitted) {
    return (
      <main className="pt-20 min-h-screen flex items-center" data-testid="booking-page">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center bg-white p-12 rounded-3xl shadow-lg"
          >
            <div className="w-20 h-20 rounded-full bg-[#FFF3E0] flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-[#D4A373]" />
            </div>
            <h2 className="text-3xl font-serif mb-4">¡Solicitud enviada!</h2>
            <div className="bg-[#FFF3E0] border-l-4 border-[#D4A373] p-4 rounded-r-lg text-left mb-6">
              <p className="font-medium text-[#D4A373]">⏳ Pendiente de confirmación</p>
              <p className="text-sm text-stone-600 mt-1">Tu cita está pendiente de confirmación por parte de nuestro equipo.</p>
            </div>
            <div className="bg-[#F5F5F4] p-4 rounded-xl text-left mb-6">
              <p className="text-sm text-stone-600">
                <strong>Servicio:</strong> {formData.servicio}<br />
                <strong>Fecha:</strong> {formData.fecha}<br />
                <strong>Hora:</strong> {formData.hora}<br />
                <strong>Modalidad:</strong> {formData.modalidad}
              </p>
            </div>
            <p className="text-stone-600 mb-6 text-sm">
              Te hemos enviado un email de confirmación. Nos pondremos en contacto contigo en las próximas <strong>24-48 horas</strong> para confirmar tu cita.
            </p>
            <Link to="/" className="btn-primary">
              Volver al inicio
            </Link>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20" data-testid="booking-page">
      <section className="section">
        <div className="container max-w-3xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span variants={fadeInUp} className="inline-block text-sm font-medium tracking-wide uppercase text-[#1B4D3E] bg-[#E0F2F1] px-4 py-2 rounded-full mb-6">
              Reservar Cita
            </motion.span>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif mb-6">
              Reserva tu consulta
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-stone-600">
              Elige el servicio, modalidad y horario que mejor te convenga. Tu solicitud será revisada y confirmada por nuestro equipo en 24-48 horas.
            </motion.p>
          </motion.div>

          {/* Progress */}
          <div className="flex justify-center mb-12">
            <div className="flex items-center gap-4">
              {[1, 2, 3].map((s) => (
                <React.Fragment key={s}>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                      step >= s ? "bg-[#1B4D3E] text-white" : "bg-stone-200 text-stone-500"
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && <div className={`w-16 h-1 ${step > s ? "bg-[#1B4D3E]" : "bg-stone-200"}`} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 rounded-3xl shadow-lg"
          >
            <form onSubmit={handleSubmit} data-testid="booking-form">
              {/* Step 1: Service & Modality */}
              {step === 1 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-serif mb-4">Selecciona el servicio</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {servicios.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setFormData({ ...formData, servicio: s })}
                        className={`p-4 rounded-xl border text-left transition-all ${
                          formData.servicio === s
                            ? "border-[#1B4D3E] bg-[#E0F2F1]"
                            : "border-stone-200 hover:border-stone-300"
                        }`}
                        data-testid={`booking-service-${s.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>

                  <h3 className="text-xl font-serif mt-8 mb-4">Modalidad</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { value: "online", label: "Online", icon: MessageCircle },
                      { value: "presencial", label: "Presencial", icon: MapPin }
                    ].map((m) => (
                      <button
                        key={m.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, modalidad: m.value })}
                        className={`p-6 rounded-xl border flex flex-col items-center gap-3 transition-all ${
                          formData.modalidad === m.value
                            ? "border-[#1B4D3E] bg-[#E0F2F1]"
                            : "border-stone-200 hover:border-stone-300"
                        }`}
                        data-testid={`booking-modality-${m.value}`}
                      >
                        <m.icon className="w-8 h-8 text-[#1B4D3E]" />
                        {m.label}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={!formData.servicio || !formData.modalidad}
                    className="btn-primary w-full mt-8"
                    data-testid="booking-next-step-1"
                  >
                    Continuar
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              )}

              {/* Step 2: Date & Time */}
              {step === 2 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-serif mb-4">Selecciona fecha y hora</h3>
                  
                  <div>
                    <label className="form-label">Fecha</label>
                    <input
                      type="date"
                      className="form-input"
                      min={minDate}
                      value={formData.fecha}
                      onChange={handleDateChange}
                      data-testid="booking-input-fecha"
                    />
                  </div>

                  {formData.fecha && (
                    <div>
                      <label className="form-label">Hora disponible</label>
                      <div className="grid grid-cols-4 gap-2">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setFormData({ ...formData, hora: slot })}
                            className={`p-3 rounded-lg border text-center transition-all ${
                              formData.hora === slot
                                ? "border-[#1B4D3E] bg-[#E0F2F1]"
                                : "border-stone-200 hover:border-stone-300"
                            }`}
                            data-testid={`booking-slot-${slot}`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 mt-8">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="btn-outline flex-1"
                    >
                      Atrás
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      disabled={!formData.fecha || !formData.hora}
                      className="btn-primary flex-1"
                      data-testid="booking-next-step-2"
                    >
                      Continuar
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Personal Info */}
              {step === 3 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-serif mb-4">Tus datos de contacto</h3>
                  
                  <div>
                    <label className="form-label">Nombre completo *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                      data-testid="booking-input-nombre"
                    />
                  </div>

                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="booking-input-email"
                    />
                  </div>

                  <div>
                    <label className="form-label">Teléfono *</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      required
                      data-testid="booking-input-telefono"
                    />
                  </div>

                  <div>
                    <label className="form-label">Mensaje adicional (opcional)</label>
                    <textarea
                      className="form-input min-h-[100px] resize-none"
                      value={formData.mensaje}
                      onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
                      placeholder="Cuéntanos brevemente el motivo de tu consulta..."
                      data-testid="booking-input-mensaje"
                    />
                  </div>

                  {/* Summary */}
                  <div className="bg-[#F5F5F4] p-4 rounded-xl mt-6">
                    <h4 className="font-medium mb-2">Resumen de tu cita</h4>
                    <p className="text-sm text-stone-600">
                      <strong>Servicio:</strong> {formData.servicio}<br />
                      <strong>Modalidad:</strong> {formData.modalidad}<br />
                      <strong>Fecha:</strong> {formData.fecha}<br />
                      <strong>Hora:</strong> {formData.hora}
                    </p>
                  </div>

                  <div className="flex gap-4 mt-8">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="btn-outline flex-1"
                    >
                      Atrás
                    </button>
                    <button
                      type="submit"
                      disabled={submitting || !formData.nombre || !formData.email || !formData.telefono}
                      className="btn-primary flex-1"
                      data-testid="booking-submit-btn"
                    >
                      {submitting ? "Reservando..." : (
                        <>
                          <Calendar className="w-5 h-5" />
                          Confirmar Cita
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

// Shop Page
const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [email, setEmail] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/shop/products`);
        setProducts(response.data.products);
        // Set featured product as default selected
        const featured = response.data.products.find(p => p.featured);
        if (featured) setSelectedProduct(featured);
      } catch (e) {
        console.error("Error fetching products:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const handleCheckout = async () => {
    if (!selectedProduct || !acceptTerms) return;
    
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/shop/checkout`, {
        product_id: selectedProduct.id,
        origin_url: window.location.origin,
        customer_email: email,
        accept_terms: acceptTerms
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (e) {
      console.error("Error creating checkout:", e);
      alert("Error al procesar el pago. Por favor, inténtalo de nuevo.");
    } finally {
      setProcessing(false);
    }
  };

  const sessionProducts = products.filter(p => p.type === "session");
  const digitalProducts = products.filter(p => p.type === "digital");

  return (
    <main className="pt-20" data-testid="shop-page">
      {/* Hero */}
      <section className="section hero-gradient">
        <div className="container">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.span variants={fadeInUp} className="inline-block text-sm font-medium tracking-wide uppercase text-[#1B4D3E] bg-[#E0F2F1] px-4 py-2 rounded-full mb-6">
              Tienda Online
            </motion.span>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-serif mb-6">
              Sesiones de Psicología Online
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-stone-600">
              Compra tus sesiones de forma segura y reserva tu cita al instante. Pago 100% seguro con Stripe.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-6 mt-6 text-sm text-stone-500">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#1B4D3E]" />
                <span>Pago seguro</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-[#1B4D3E]" />
                <span>Tarjeta, Bizum, Apple Pay, Google Pay</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="section bg-white">
        <div className="container">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-[#E0F2F1] border-t-[#1B4D3E] rounded-full animate-spin mx-auto"></div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Products List */}
              <div className="lg:col-span-2">
                {/* Session Products */}
                <div className="mb-12">
                  <h2 className="text-2xl font-serif mb-6 flex items-center gap-3">
                    <Brain className="w-6 h-6 text-[#1B4D3E]" />
                    Sesiones de Terapia
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {sessionProducts.map((product) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-white border-2 rounded-2xl overflow-hidden cursor-pointer transition-all ${
                          selectedProduct?.id === product.id
                            ? "border-[#1B4D3E] shadow-lg"
                            : "border-stone-200 hover:border-stone-300"
                        }`}
                        onClick={() => setSelectedProduct(product)}
                        data-testid={`product-${product.id}`}
                      >
                        <div className="relative">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-40 object-cover"
                          />
                          {product.featured && (
                            <span className="absolute top-3 right-3 bg-[#D4A373] text-white text-xs font-medium px-3 py-1 rounded-full">
                              Más popular
                            </span>
                          )}
                          {product.savings && (
                            <span className="absolute top-3 left-3 bg-[#1B4D3E] text-white text-xs font-medium px-3 py-1 rounded-full">
                              Ahorras {product.savings}€
                            </span>
                          )}
                        </div>
                        <div className="p-5">
                          <h3 className="font-serif text-lg mb-2">{product.name}</h3>
                          <p className="text-stone-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-[#1B4D3E]">{product.price.toFixed(0)}€</span>
                            {selectedProduct?.id === product.id && (
                              <CheckCircle className="w-6 h-6 text-[#1B4D3E]" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Digital Products */}
                <div>
                  <h2 className="text-2xl font-serif mb-6 flex items-center gap-3">
                    <Download className="w-6 h-6 text-[#1B4D3E]" />
                    Recursos Digitales
                  </h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    {digitalProducts.map((product) => (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`bg-white border-2 rounded-2xl overflow-hidden cursor-pointer transition-all ${
                          selectedProduct?.id === product.id
                            ? "border-[#1B4D3E] shadow-lg"
                            : "border-stone-200 hover:border-stone-300"
                        }`}
                        onClick={() => setSelectedProduct(product)}
                        data-testid={`product-${product.id}`}
                      >
                        <div className="relative">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-40 object-cover"
                          />
                          <span className="absolute top-3 right-3 bg-stone-800 text-white text-xs font-medium px-3 py-1 rounded-full">
                            {product.format}
                          </span>
                        </div>
                        <div className="p-5">
                          <h3 className="font-serif text-lg mb-2">{product.name}</h3>
                          <p className="text-stone-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-[#1B4D3E]">{product.price.toFixed(0)}€</span>
                            {selectedProduct?.id === product.id && (
                              <CheckCircle className="w-6 h-6 text-[#1B4D3E]" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Checkout Sidebar */}
              <div className="lg:col-span-1">
                <div className="bg-white border border-stone-200 rounded-2xl p-6 sticky top-28">
                  <h3 className="text-xl font-serif mb-6">Tu pedido</h3>
                  
                  {selectedProduct ? (
                    <>
                      <div className="flex items-start gap-4 mb-6 pb-6 border-b border-stone-100">
                        <img
                          src={selectedProduct.image}
                          alt={selectedProduct.name}
                          className="w-20 h-20 rounded-lg object-cover"
                        />
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{selectedProduct.name}</h4>
                          <p className="text-stone-500 text-xs mt-1">
                            {selectedProduct.type === "session" ? `${selectedProduct.sessions} sesión(es)` : selectedProduct.format}
                          </p>
                        </div>
                        <span className="font-bold text-[#1B4D3E]">{selectedProduct.price.toFixed(0)}€</span>
                      </div>

                      <div className="space-y-4 mb-6">
                        <div>
                          <label className="form-label">Email para confirmación</label>
                          <input
                            type="email"
                            className="form-input"
                            placeholder="tu@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="checkout-email"
                          />
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-1 w-4 h-4 text-[#1B4D3E] rounded border-stone-300 focus:ring-[#1B4D3E]"
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            data-testid="checkout-terms"
                          />
                          <span className="text-xs text-stone-600">
                            Acepto la <a href="/privacidad" className="text-[#1B4D3E] underline">política de privacidad</a> y las <a href="/condiciones" className="text-[#1B4D3E] underline">condiciones de contratación</a>. Entiendo que mis datos serán tratados conforme al RGPD.
                          </span>
                        </label>
                      </div>

                      <div className="flex justify-between items-center mb-6 text-lg font-bold">
                        <span>Total</span>
                        <span className="text-[#1B4D3E]">{selectedProduct.price.toFixed(2)}€</span>
                      </div>

                      <button
                        onClick={handleCheckout}
                        disabled={!acceptTerms || processing}
                        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="checkout-button"
                      >
                        {processing ? (
                          "Procesando..."
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5" />
                            Pagar con Stripe
                          </>
                        )}
                      </button>

                      <div className="mt-6 space-y-3">
                        <div className="flex items-center gap-2 text-xs text-stone-500">
                          <Shield className="w-4 h-4" />
                          <span>Pago 100% seguro con encriptación SSL</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-stone-500">
                          <CreditCard className="w-4 h-4" />
                          <span>Visa, Mastercard, Bizum, Apple Pay, Google Pay</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-stone-500 text-center py-8">
                      Selecciona un producto para continuar
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Trust Section */}
      <section className="section bg-[#F5F5F4]">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="w-12 h-12 text-[#1B4D3E] mx-auto mb-6" />
            <h2 className="text-2xl font-serif mb-4">Tu tranquilidad es nuestra prioridad</h2>
            <p className="text-stone-600 mb-8">
              Entendemos que dar el paso de buscar ayuda requiere confianza. Por eso garantizamos:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-2xl">
                <CheckCircle className="w-8 h-8 text-[#1B4D3E] mx-auto mb-3" />
                <h3 className="font-medium mb-2">Confidencialidad total</h3>
                <p className="text-sm text-stone-600">Tus datos están protegidos bajo secreto profesional y RGPD.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl">
                <CreditCard className="w-8 h-8 text-[#1B4D3E] mx-auto mb-3" />
                <h3 className="font-medium mb-2">Pago seguro</h3>
                <p className="text-sm text-stone-600">Procesado por Stripe, líder mundial en pagos seguros.</p>
              </div>
              <div className="bg-white p-6 rounded-2xl">
                <Calendar className="w-8 h-8 text-[#1B4D3E] mx-auto mb-3" />
                <h3 className="font-medium mb-2">Reserva inmediata</h3>
                <p className="text-sm text-stone-600">Tras el pago, recibirás acceso para reservar tu cita.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Shop Confirmation Page
const ShopConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/shop/checkout/status/${sessionId}`);
        setStatus(response.data);
      } catch (e) {
        console.error("Error checking status:", e);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, [sessionId]);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#E0F2F1] border-t-[#1B4D3E] rounded-full animate-spin"></div>
      </main>
    );
  }

  if (!sessionId || !status) {
    return (
      <main className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-serif mb-4">Sesión no encontrada</h1>
          <Link to="/tienda" className="btn-primary">Volver a la tienda</Link>
        </div>
      </main>
    );
  }

  const isSuccess = status.payment_status === "paid";

  return (
    <main className="pt-20 min-h-screen flex items-center" data-testid="confirmation-page">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center bg-white p-12 rounded-3xl shadow-lg"
        >
          {isSuccess ? (
            <>
              <div className="w-20 h-20 rounded-full bg-[#E0F2F1] flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-[#1B4D3E]" />
              </div>
              <h2 className="text-3xl font-serif mb-4">¡Pago completado!</h2>
              <p className="text-stone-600 mb-6">
                Gracias por tu compra de <strong>{status.product_name}</strong>.
              </p>
              
              <div className="bg-[#F5F5F4] p-6 rounded-xl mb-6 text-left">
                <p className="text-sm text-stone-600 mb-2">
                  <strong>Producto:</strong> {status.product_name}
                </p>
                <p className="text-sm text-stone-600">
                  <strong>Importe:</strong> {(status.amount_total / 100).toFixed(2)}€
                </p>
              </div>

              <div className="bg-[#FFF3E0] border-l-4 border-[#D4A373] p-4 rounded-r-lg text-left mb-6">
                <p className="font-medium text-[#D4A373] mb-1">📧 Revisa tu email</p>
                <p className="text-sm text-stone-600">
                  Te hemos enviado la confirmación con instrucciones para reservar tu cita.
                </p>
              </div>

              <div className="space-y-3">
                <Link
                  to="/reservar"
                  className="btn-primary w-full"
                  data-testid="book-appointment-btn"
                >
                  <Calendar className="w-5 h-5" />
                  Reservar mi cita ahora
                </Link>
                <Link
                  to="/"
                  className="btn-outline w-full"
                >
                  Volver al inicio
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                <X className="w-10 h-10 text-red-500" />
              </div>
              <h2 className="text-3xl font-serif mb-4">Pago no completado</h2>
              <p className="text-stone-600 mb-6">
                El pago no se ha podido procesar. Por favor, inténtalo de nuevo.
              </p>
              <Link
                to="/tienda"
                className="btn-primary"
              >
                Volver a la tienda
              </Link>
            </>
          )}
        </motion.div>
      </div>
    </main>
  );
};

// ============== ADMIN PANEL COMPONENTS ==============

// Admin Auth Context
const useAdminAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(() => setIsAuthenticated(true))
      .catch(() => {
        localStorage.removeItem('admin_token');
        setIsAuthenticated(false);
      })
      .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/admin/login`, { email, password });
    localStorage.setItem('admin_token', response.data.access_token);
    setIsAuthenticated(true);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
  };

  return { isAuthenticated, isLoading, login, logout };
};

// Admin Login Page
const AdminLoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onLogin(email, password);
    } catch (err) {
      setError('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-stone-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-2xl font-serif text-stone-800">Panel de Administración</h1>
          <p className="text-stone-500 mt-2">Casaleiz Psicología</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="admin@email.com"
              required
              data-testid="admin-login-email"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="••••••••"
              required
              data-testid="admin-login-password"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? 'Accediendo...' : 'Acceder'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <Link to="/" className="text-teal-600 hover:underline text-sm">
            ← Volver a la web
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, transRes, apptRes, contRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`, getAuthHeaders()),
        axios.get(`${API}/admin/transactions`, getAuthHeaders()),
        axios.get(`${API}/admin/appointments`, getAuthHeaders()),
        axios.get(`${API}/admin/contacts`, getAuthHeaders())
      ]);
      setStats(statsRes.data);
      setTransactions(transRes.data.transactions);
      setAppointments(apptRes.data.appointments);
      setContacts(contRes.data.contacts);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateAppointmentStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/appointments/${id}`, { status }, getAuthHeaders());
      loadData();
    } catch (err) {
      console.error('Error updating appointment:', err);
    }
  };

  const markContactAsRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}/read`, {}, getAuthHeaders());
      loadData();
    } catch (err) {
      console.error('Error marking contact:', err);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'sales', label: 'Ventas', icon: DollarSign },
    { id: 'appointments', label: 'Citas', icon: Calendar },
    { id: 'contacts', label: 'Mensajes', icon: MessageSquare }
  ];

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Admin Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="h-10 w-auto" />
            <span className="text-lg font-medium text-stone-800">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-stone-600 hover:text-teal-600 text-sm">
              Ver web
            </Link>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 text-stone-600 hover:text-red-600 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-stone-600 hover:bg-stone-100'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && stats && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <span className="text-stone-500 text-sm">Ingresos del mes</span>
                    </div>
                    <p className="text-3xl font-bold text-stone-800">{stats.monthly_revenue.toFixed(2)}€</p>
                    <p className="text-sm text-stone-500 mt-1">{stats.monthly_transactions} transacciones</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-stone-500 text-sm">Ingresos totales</span>
                    </div>
                    <p className="text-3xl font-bold text-stone-800">{stats.total_revenue.toFixed(2)}€</p>
                    <p className="text-sm text-stone-500 mt-1">{stats.total_transactions} transacciones</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Calendar className="w-5 h-5 text-amber-600" />
                      </div>
                      <span className="text-stone-500 text-sm">Citas pendientes</span>
                    </div>
                    <p className="text-3xl font-bold text-stone-800">{stats.pending_appointments}</p>
                    <p className="text-sm text-stone-500 mt-1">Por confirmar</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-purple-600" />
                      </div>
                      <span className="text-stone-500 text-sm">Mensajes sin leer</span>
                    </div>
                    <p className="text-3xl font-bold text-stone-800">{stats.unread_contacts}</p>
                    <p className="text-sm text-stone-500 mt-1">Formulario contacto</p>
                  </div>
                </div>

                {/* Product Sales */}
                {stats.product_sales.length > 0 && (
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-stone-100">
                    <h3 className="font-semibold text-stone-800 mb-4">Ventas por producto</h3>
                    <div className="space-y-3">
                      {stats.product_sales.map((product, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0">
                          <span className="text-stone-700">{product.name}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-stone-500">{product.count} vendidos</span>
                            <span className="font-semibold text-teal-600">{product.revenue.toFixed(2)}€</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sales Tab */}
            {activeTab === 'sales' && (
              <div className="bg-white rounded-xl shadow-sm border border-stone-100 overflow-hidden">
                <div className="p-4 border-b border-stone-100">
                  <h3 className="font-semibold text-stone-800">Historial de ventas</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Fecha</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Producto</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Cliente</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Importe</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-stone-600">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="px-4 py-8 text-center text-stone-500">
                            No hay transacciones registradas
                          </td>
                        </tr>
                      ) : (
                        transactions.map((tx, idx) => (
                          <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50">
                            <td className="px-4 py-3 text-sm text-stone-600">
                              {new Date(tx.created_at).toLocaleDateString('es-ES')}
                            </td>
                            <td className="px-4 py-3 text-sm text-stone-800 font-medium">
                              {tx.product_name}
                            </td>
                            <td className="px-4 py-3 text-sm text-stone-600">
                              {tx.customer_email || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-stone-800">
                              {tx.amount.toFixed(2)}€
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                tx.payment_status === 'completed' 
                                  ? 'bg-green-100 text-green-700'
                                  : tx.payment_status === 'failed'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {tx.payment_status === 'completed' ? 'Completado' : 
                                 tx.payment_status === 'failed' ? 'Fallido' : 'Pendiente'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
              <div className="bg-white rounded-xl shadow-sm border border-stone-100 overflow-hidden">
                <div className="p-4 border-b border-stone-100">
                  <h3 className="font-semibold text-stone-800">Gestión de citas</h3>
                </div>
                <div className="divide-y divide-stone-100">
                  {appointments.length === 0 ? (
                    <div className="px-4 py-8 text-center text-stone-500">
                      No hay citas registradas
                    </div>
                  ) : (
                    appointments.map((apt, idx) => (
                      <div key={idx} className="p-4 hover:bg-stone-50">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-stone-800">{apt.nombre}</span>
                              <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                                apt.status === 'confirmada' 
                                  ? 'bg-green-100 text-green-700'
                                  : apt.status === 'cancelada'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {apt.status || 'pendiente'}
                              </span>
                            </div>
                            <p className="text-sm text-stone-600">
                              📅 {apt.fecha} a las {apt.hora} · {apt.servicio}
                            </p>
                            <p className="text-sm text-stone-500">
                              ✉️ {apt.email} · 📞 {apt.telefono}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {apt.status !== 'confirmada' && (
                              <button
                                onClick={() => updateAppointmentStatus(apt.id, 'confirmada')}
                                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
                                data-testid={`confirm-apt-${apt.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                                Confirmar
                              </button>
                            )}
                            {apt.status !== 'cancelada' && (
                              <button
                                onClick={() => updateAppointmentStatus(apt.id, 'cancelada')}
                                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
                                data-testid={`cancel-apt-${apt.id}`}
                              >
                                <X className="w-4 h-4" />
                                Cancelar
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === 'contacts' && (
              <div className="bg-white rounded-xl shadow-sm border border-stone-100 overflow-hidden">
                <div className="p-4 border-b border-stone-100">
                  <h3 className="font-semibold text-stone-800">Mensajes de contacto</h3>
                </div>
                <div className="divide-y divide-stone-100">
                  {contacts.length === 0 ? (
                    <div className="px-4 py-8 text-center text-stone-500">
                      No hay mensajes de contacto
                    </div>
                  ) : (
                    contacts.map((contact, idx) => (
                      <div key={idx} className={`p-4 ${!contact.read ? 'bg-teal-50' : 'hover:bg-stone-50'}`}>
                        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-stone-800">{contact.nombre}</span>
                              {!contact.read && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-teal-600 text-white">
                                  Nuevo
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-stone-600 mb-2">
                              ✉️ {contact.email} {contact.telefono && `· 📞 ${contact.telefono}`}
                            </p>
                            <p className="text-stone-700 bg-stone-100 p-3 rounded-lg text-sm">
                              {contact.mensaje}
                            </p>
                            <p className="text-xs text-stone-400 mt-2">
                              {new Date(contact.created_at).toLocaleString('es-ES')}
                            </p>
                          </div>
                          {!contact.read && (
                            <button
                              onClick={() => markContactAsRead(contact.id)}
                              className="px-3 py-1.5 bg-stone-200 text-stone-700 text-sm rounded-lg hover:bg-stone-300 transition-colors flex items-center gap-1"
                              data-testid={`mark-read-${contact.id}`}
                            >
                              <Eye className="w-4 h-4" />
                              Marcar leído
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Admin Page Wrapper
const AdminPage = () => {
  const { isAuthenticated, isLoading, login, logout } = useAdminAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLoginPage onLogin={login} />;
  }

  return <AdminDashboard onLogout={logout} />;
};

// Main App
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/servicios" element={<ServicesPage />} />
          <Route path="/sobre-nosotros" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/reservar" element={<BookingPage />} />
          <Route path="/tienda" element={<ShopPage />} />
          <Route path="/tienda/confirmacion" element={<ShopConfirmationPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
        <Footer />
        <WhatsAppButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
